import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import api from '@/api';
import toast from 'react-hot-toast';

const InputField = ({ label, error, ...props }) => (
    <div>
        <label htmlFor={props.name} className="block text-sm font-medium text-pro-dark-text-secondary">{label}</label>
        <input 
            {...props}
            className={`mt-1 w-full p-2 bg-pro-dark-surface border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent ${error ? 'border-red-500' : 'border-pro-dark-border'}`}
        />
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
);

const AjouterCandidatPage = () => {
  const [formData, setFormData] = useState({});
  const [fileToUpload, setFileToUpload] = useState(null);
  const [errors, setErrors] = useState({}); // État pour stocker les erreurs
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseInt(value, 10) : value }));
    // Efface l'erreur du champ dès que l'utilisateur commence à taper
    if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChangeForIA = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsLoadingFile(true);
    const loadingToast = toast.loading('Analyse du CV en cours...');
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      const response = await api.post('/candidats/extract-from-cv', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(response.data); 
      toast.success('Formulaire pré-rempli !', { id: loadingToast });
    } catch (error) {
      console.error("Erreur lors de l'extraction des données du CV:", error);
      toast.error('Impossible d\'analyser le CV.', { id: loadingToast });
    } finally {
      setIsLoadingFile(false);
    }
  };

  // CORRECTION: Ajout de la fonction manquante
  const handleFileChangeForUpload = (event) => {
    setFileToUpload(event.target.files[0]);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.prenom) newErrors.prenom = "Le prénom est requis.";
    if (!formData.nom) newErrors.nom = "Le nom est requis.";
    if (!formData.numeroBullhorn) newErrors.numeroBullhorn = "Le N° Bullhorn est requis.";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
        toast.error("Veuillez remplir tous les champs obligatoires.");
        return;
    }
    
    setIsSaving(true);
    const submissionData = new FormData();
    submissionData.append('formData', JSON.stringify(formData));
    if (fileToUpload) {
      submissionData.append('cv_file', fileToUpload);
    }

    try {
      await api.post('/candidats/', submissionData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Candidat ajouté avec succès !');
      navigate('/candidats');
    } catch (error) {
      console.error("Erreur lors de l'ajout du candidat:", error.response?.data);
      toast.error(`Une erreur est survenue: ${error.response?.data?.detail || 'Erreur inconnue'}`);
    } finally {
      setIsSaving(false);
    }
  };
  
  const fields = [
    { label: 'Prénom', name: 'prenom' },
    { label: 'Nom', name: 'nom' },
    { label: 'N° Bullhorn', name: 'numeroBullhorn' },
    { label: 'Sourcer', name: 'sourcer' }, { label: 'Profil', name: 'profil' },
    { label: 'EP/EM', name: 'epem' }, { label: 'Mobilité', name: 'mobilite' },
    { label: 'Prise de Réf', name: 'priseDeRef' },
    { label: 'Format Pixie', name: 'formatPixie' }, { label: 'XP', name: 'xp', type: 'number' },
    { label: 'Dispo', name: 'dispo', type: 'date' }, { label: 'Salaire actuel', name: 'salaireActuel' },
    { label: 'Statut', name: 'statut' }, { label: 'PS', name: 'ps' },
    { label: 'Situation process', name: 'situationProcess' }, { label: 'Titre de séjour', name: 'titreDeSejour' },
    { label: 'Anglais', name: 'anglais' }, { label: 'Poste Actuel', name: 'posteActuel' },
    { label: 'Piste Pixie', name: 'pistePixie' }
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold text-pro-dark-text-primary mb-8">Ajouter un nouveau candidat</h1>
      <Card title="Pré-remplir avec un CV">
        <p className="text-pro-dark-text-secondary mb-4">Importez un fichier CV (PDF, DOCX) pour que l'IA remplisse le formulaire.</p>
        <input type="file" onChange={handleFileChangeForIA} disabled={isLoadingFile} accept=".pdf,.docx" className="text-sm text-pro-dark-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pro-dark-accent file:text-white hover:file:bg-pro-dark-accent-hover"/>
        {isLoadingFile && <p className="text-pro-dark-accent animate-pulse mt-2">Analyse du CV en cours...</p>}
      </Card>
      <div className="my-8 text-center text-pro-dark-text-secondary"><span className="bg-pro-dark-surface px-2">OU</span></div>
      <Card title="Remplir manuellement">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {fields.map(field => (
            <InputField 
                key={field.name} 
                {...field}
                value={formData[field.name] || ''} 
                onChange={handleInputChange}
                error={errors[field.name]} // Passe l'erreur au composant
            />
          ))}
          <div className="md:col-span-3">
            <label htmlFor="competences" className="block text-sm font-medium text-pro-dark-text-secondary">Compétences</label>
            <textarea id="competences" name="competences" rows="4" value={formData.competences || ''} onChange={handleInputChange} className="mt-1 w-full p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"></textarea>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-pro-dark-text-secondary">Joindre le CV (pour sauvegarde)</label>
            <input 
              type="file"
              onChange={handleFileChangeForUpload}
              accept=".pdf,.docx"
              className="mt-1 w-full text-sm text-pro-dark-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pro-dark-surface file:text-white hover:file:bg-pro-dark-border"
            />
          </div>
          <div className="md:col-span-3 text-right">
            <button type="submit" disabled={isSaving} className="px-6 py-2 bg-pro-dark-accent text-white rounded-lg hover:bg-pro-dark-accent-hover disabled:bg-gray-600">
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AjouterCandidatPage;